/* oxlint-disable sorare/enforce-monetary-reward-disclaimer -- Non-reward currency (wallet/market/JONUM limits/etc.) — disclaimer does not apply. */
import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Cryptocurrency } from '@sorare/core/src/__generated__/globalTypes';
import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { Dropdown } from '@sorare/core/src/atoms/dropdowns/Dropdown';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { ChevronRightBold } from '@sorare/core/src/atoms/icons/ChevronRightBold';
import { iconInfoCircle } from '@sorare/core/src/atoms/icons/system/iconInfoCircle.ts';
import { RadioGroup } from '@sorare/core/src/atoms/inputs/RadioGroup';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import { BodyS } from '@sorare/core/src/atoms/typography';
import { Bold } from '@sorare/core/src/atoms/typography/Bold';
import { CreateFiatWalletWrapper } from '@sorare/core/src/components/fiatWallet/CreateFiatWalletWrapper';
import { CryptoWallet } from '@sorare/core/src/components/paymentMethods/CryptoWallet';
import { FiatWallet } from '@sorare/core/src/components/paymentMethods/FiatWallet';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useIntlContext } from '@sorare/core/src/contexts/intl';
import {
  WalletTab,
  useWalletDrawerContext,
} from '@sorare/core/src/contexts/walletDrawer';
import {
  AcceptedCurrenciesValue,
  useAcceptedCurrencies,
} from '@sorare/core/src/hooks/useAcceptedCurrencies';
import { useCryptoAmount } from '@sorare/core/src/hooks/useCryptoAmount';
import { useCurrentUserCurrencies } from '@sorare/core/src/hooks/useCurrentUserCurrencies';
import { useFeatureFlags } from '@sorare/core/src/hooks/useFeatureFlags';
import { useFiatBalance } from '@sorare/core/src/hooks/wallets/useFiatBalance';
import { usePreferredPaymentMethod } from '@sorare/core/src/hooks/wallets/usePreferredPaymentMethod';
import { wallet } from '@sorare/core/src/lib/glossary';
import {
  PaymentMethod,
  WalletPaymentMethod,
} from '@sorare/core/src/lib/paymentMethod';

import { setCurrencyAndPaymentMethod } from '#src/components/directOffer/NewOfferBuilder/actions.ts';
import type {
  CardDataType,
  StateProps,
} from '#src/components/directOffer/NewOfferBuilder/types.ts';
import { useHasInsufficientFundsInWallets } from '#src/hooks/useHasInsufficientFundsInWallets.ts';

import type { TradePaymentMethods_publicUserInfoInterface } from './__generated__/index.graphql.ts';

const Frame = styled(ButtonBase)`
  display: flex;
  flex-direction: column;
  gap: var(--unit);
  width: 100%;
  padding: var(--double-unit);
  border: 1px solid var(--c-nd-200);
  border-radius: var(--unit);
  background-color: var(--c-nd-100);
`;
const Row = styled(SBHorizontal).attrs({ gap: 0 })`
  width: 100%;
  text-align: left;
`;
const Selected = styled(Vertical).attrs({ gap: 0 })`
  justify-content: flex-start;
`;

const StyledChevronRightBold = styled(ChevronRightBold)`
  transform: rotate(0);
  &.expanded {
    transform: rotate(90deg);
  }
  transition: transform 0.25s ease-out;
`;
const Text = styled(Vertical).attrs({ gap: 0 })`
  align-items: flex-start;
`;

const SimpleButton = styled(ButtonBase)`
  color: var(--c-link);
  text-decoration: underline;
`;

const HelperRoot = styled(Horizontal).attrs({ gap: 1.5 })`
  width: 100%;
  background-color: rgba(var(--c-rgb-red-400), 0.05);
  padding: var(--unit);
  border-radius: var(--unit);
  border: solid 1px var(--c-red-400);
`;

const WarningRoot = styled.div`
  width: 100%;
  padding: 0 var(--double-unit) var(--unit);
`;

const Warning = styled(Horizontal)`
  padding: var(--intermediate-unit);
  background-color: var(--c-nd-100);
  border: 1px solid var(--c-nd-200);
  border-radius: var(--unit);
  color: var(--c-white);
`;

const HelperPadding = styled.div`
  padding: 0 var(--double-unit) var(--double-unit);
`;

const Helper = ({
  diffAmount,
  onClick,
}: {
  diffAmount?: string;
  onClick: () => void;
}) => (
  <HelperRoot>
    <FontAwesomeIcon icon={iconInfoCircle} color="var(--c-red-400)" />
    <Text>
      <BodyS color="var(--c-white)">
        <FormattedMessage
          defaultMessage="You’ll need to add {diffAmount} to your wallet to send this trade."
          values={{
            diffAmount,
          }}
        />
      </BodyS>
      <SimpleButton
        onClick={e => {
          e.preventDefault();
          onClick();
        }}
      >
        <BodyS bold color="var(--c-link)">
          <FormattedMessage {...wallet.addFunds} />
        </BodyS>
      </SimpleButton>
    </Text>
  </HelperRoot>
);

export const TradePaymentMethods = <D extends CardDataType>({
  state: { sendAmount, paymentMethod: selectedPaymentMethodFromstateProps },
  dispatch,
  onClose,
  to,
}: StateProps<D> & {
  onClose: () => void;
  to: TradePaymentMethods_publicUserInfoInterface;
}) => {
  const {
    flags: { useCannotSwapCrytpo = true },
  } = useFeatureFlags();

  const selectedPaymentMethod = (() => {
    if (
      selectedPaymentMethodFromstateProps === WalletPaymentMethod.ETH_WALLET
    ) {
      return WalletPaymentMethod.CRYPTO_WALLET;
    }
    return selectedPaymentMethodFromstateProps;
  })();

  const [expanded, setExpanded] = useState(false);
  const { setCurrentTab, showDrawer } = useWalletDrawerContext();
  const { formatNumber } = useIntlContext();
  const { acceptedCurrencies } = useAcceptedCurrencies({ user: to });

  const {
    walletPreferences: { showCryptoWallet, showFiatWallet },
  } = useCurrentUserContext();
  const { canDepositAndWithdraw } = useFiatBalance();

  const { fiatCurrency, cryptocurrency, cryptocurrencySymbol } =
    useCurrentUserCurrencies();

  const toCryptocurrency = to.cryptocurrency || Cryptocurrency.WEI;

  const { preferredPaymentMethod } = usePreferredPaymentMethod();
  const hasInsufficientFundsInWallets = useHasInsufficientFundsInWallets();
  const {
    insufficientFundsInCryptoWallet,
    diffInSubUnitForCryptoWallet,
    insufficientFundsInFiatWallet,
    diffInFiatCentsForFiatWallet,
  } = hasInsufficientFundsInWallets(sendAmount);
  const { formatCryptoAmount, maximumFractionDigits } =
    useCryptoAmount(cryptocurrency);

  const canSwapCrypto =
    (useCannotSwapCrytpo && cryptocurrency === toCryptocurrency) ||
    !useCannotSwapCrytpo;

  const acceptCryptoWallet =
    [AcceptedCurrenciesValue.BOTH, AcceptedCurrenciesValue.CRYPTO].includes(
      acceptedCurrencies
    ) && canSwapCrypto;

  const acceptCashWallet = [
    AcceptedCurrenciesValue.BOTH,
    AcceptedCurrenciesValue.FIAT,
  ].includes(acceptedCurrencies);

  useEffect(() => {
    if (selectedPaymentMethod === null) {
      if (!acceptCryptoWallet) {
        dispatch(
          setCurrencyAndPaymentMethod({
            paymentMethod: WalletPaymentMethod.FIAT_WALLET,
            referenceCurrency: fiatCurrency,
          })
        );
        return;
      }
      if (!acceptCashWallet) {
        dispatch(
          setCurrencyAndPaymentMethod({
            paymentMethod: WalletPaymentMethod.CRYPTO_WALLET,
            referenceCurrency: cryptocurrency,
          })
        );
        return;
      }
      if (showCryptoWallet) {
        dispatch(
          setCurrencyAndPaymentMethod({
            paymentMethod:
              preferredPaymentMethod === PaymentMethod.FIAT_WALLET
                ? WalletPaymentMethod.FIAT_WALLET
                : WalletPaymentMethod.CRYPTO_WALLET,
            referenceCurrency:
              preferredPaymentMethod === PaymentMethod.FIAT_WALLET
                ? fiatCurrency
                : cryptocurrency,
          })
        );
        return;
      }
      dispatch(
        setCurrencyAndPaymentMethod({
          paymentMethod: WalletPaymentMethod.FIAT_WALLET,
          referenceCurrency: fiatCurrency,
        })
      );
    }
  }, [
    fiatCurrency,
    cryptocurrency,
    preferredPaymentMethod,
    dispatch,
    selectedPaymentMethod,
    acceptCryptoWallet,
    acceptCashWallet,
    acceptedCurrencies,
    showCryptoWallet,
  ]);

  if (!selectedPaymentMethod) return null;

  const onAddFunds = (tab: WalletTab) => {
    onClose();
    setCurrentTab(tab);
    showDrawer();
  };

  const cryptoWalletHelper = (
    <Helper
      diffAmount={formatCryptoAmount(
        (diffInSubUnitForCryptoWallet || '0').toString(),
        undefined,
        {
          maximumFractionDigits,
        }
      )}
      onClick={() => onAddFunds(WalletTab.ADD_FUNDS_TO_CRYPTO_WALLET)}
    />
  );

  const fiatWalletHelper = (
    <CreateFiatWalletWrapper canDismissAfterActivation={false}>
      {({ setShowCreateFiatWallet }) => (
        <Helper
          diffAmount={formatNumber((diffInFiatCentsForFiatWallet || 0) / 100, {
            style: 'currency',
            currency: fiatCurrency,
          })}
          onClick={() => {
            if (canDepositAndWithdraw) {
              onAddFunds(WalletTab.ADD_FUNDS_TO_FIAT_WALLET);
            } else {
              setShowCreateFiatWallet(true);
            }
          }}
        />
      )}
    </CreateFiatWalletWrapper>
  );
  const paymentMethods = {
    [WalletPaymentMethod.CRYPTO_WALLET]: {
      label: <CryptoWallet />,
      disabled: !acceptCryptoWallet,
      value: WalletPaymentMethod.CRYPTO_WALLET,
      referenceCurrency: cryptocurrency,
      helper: (
        <>
          {!!insufficientFundsInCryptoWallet && !!acceptCryptoWallet && (
            <HelperPadding>{cryptoWalletHelper}</HelperPadding>
          )}
          {!acceptCryptoWallet && (
            <WarningRoot>
              <Warning>
                <FontAwesomeIcon icon={iconInfoCircle} />
                <BodyS>
                  <FormattedMessage
                    defaultMessage="<b>{nickname}</b> can't accept {cryptocurrencySymbol}."
                    values={{
                      b: Bold,
                      nickname: to.nickname,
                      cryptocurrencySymbol,
                    }}
                  />
                </BodyS>
              </Warning>
            </WarningRoot>
          )}
        </>
      ),
    },
    ...(showFiatWallet
      ? {
          [WalletPaymentMethod.FIAT_WALLET]: {
            label: <FiatWallet />,
            disabled: !acceptCashWallet,
            value: WalletPaymentMethod.FIAT_WALLET,
            referenceCurrency: fiatCurrency,
            helper: (
              <>
                {!!insufficientFundsInFiatWallet && !!acceptCashWallet && (
                  <HelperPadding>{fiatWalletHelper}</HelperPadding>
                )}
                {!acceptCashWallet && (
                  <WarningRoot>
                    <Warning>
                      <FontAwesomeIcon icon={iconInfoCircle} />
                      <BodyS>
                        <FormattedMessage
                          defaultMessage="<b>{nickname}</b> can't accept Cash."
                          values={{ b: Bold, nickname: to.nickname }}
                        />
                      </BodyS>
                    </Warning>
                  </WarningRoot>
                )}
              </>
            ),
          },
        }
      : {}),
  };

  const paymentOptions = [
    ...(showCryptoWallet
      ? [paymentMethods[WalletPaymentMethod.CRYPTO_WALLET]]
      : []),
    paymentMethods[WalletPaymentMethod.FIAT_WALLET],
  ].filter(Boolean);

  const disabled = paymentOptions.length <= 1;

  return (
    <Vertical>
      <Dropdown
        fullWidth
        align="right"
        gap={8}
        onOpen={() => setExpanded(true)}
        onClose={() => setExpanded(false)}
        label={props => (
          <Frame {...props} disabled={disabled} disableDebounce>
            <Row>
              <Selected>
                {paymentMethods[selectedPaymentMethod]?.label}
              </Selected>
              {!disabled && (
                <StyledChevronRightBold className={classNames({ expanded })} />
              )}
            </Row>
          </Frame>
        )}
      >
        {({ closeDropdown }) => (
          <RadioGroup<WalletPaymentMethod>
            options={paymentOptions}
            value={paymentMethods[selectedPaymentMethod]?.value}
            name="select-trade-payment-method"
            onChange={(value: WalletPaymentMethod) => {
              const v =
                value === WalletPaymentMethod.ETH_WALLET
                  ? WalletPaymentMethod.CRYPTO_WALLET
                  : value;
              const method = paymentMethods[v];
              if (method) {
                dispatch(
                  setCurrencyAndPaymentMethod({
                    referenceCurrency: method.referenceCurrency,
                    paymentMethod: method.value,
                  })
                );
                closeDropdown();
              }
            }}
          />
        )}
      </Dropdown>
      {!!insufficientFundsInCryptoWallet &&
        selectedPaymentMethod === WalletPaymentMethod.CRYPTO_WALLET &&
        cryptoWalletHelper}
      {!!insufficientFundsInFiatWallet &&
        selectedPaymentMethod === WalletPaymentMethod.FIAT_WALLET &&
        fiatWalletHelper}
    </Vertical>
  );
};

TradePaymentMethods.fragments = {
  publicUserInfoInterface: gql`
    fragment TradePaymentMethods_publicUserInfoInterface on PublicUserInfoInterface {
      slug
      nickname
      cryptocurrency
      ...useAcceptedCurrencies_publicUserInfoInterface
    }
    ${useAcceptedCurrencies.fragments.publicUserInfoInterface}
  ` as TypedDocumentNode<TradePaymentMethods_publicUserInfoInterface>,
};
